import { useRef, useContext, useState } from "react";
import { Box, Menu, MenuItem, IconButton } from "@mui/material";
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Inject,
  Toolbar,
  ExcelExport,
  Filter,
  Selection,
  Sort,
  Page,
} from "@syncfusion/ej2-react-grids";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { selectedContext } from "../../pages/ShipmentsReport";
import Money from "../../assets/images/money.png";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FrameDown from "../../assets/icons/FrameDown.png";
import ExcelExportIcon from "../../assets/icons/ExcelExport.png";

// import NewPrintBtn from "../utilities/NewPrintBtn";

const CustomLink = styled(Link)`
  color: var(--primary-color);
  font-weight: 500;
  text-decoration: none;
`;

const ExportButton = styled.button`
  width: 200px;
  height: 57px;
  border-radius: 12px;
  border: 1px solid #ae1a1a;
  background-color: #ae1a1a;
  color: #ffffff;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 14px;
  box-shadow: 0px 3px 6px #0000005e;
  cursor: pointer;
`;

const ExportContainer = styled.div`
  position: absolute;
  ${(props) => (props.isRTL ? "top: 175px;" : "top: 155px;")}
  ${(props) => (props.isRTL ? "right: 320px;" : "left: 320px;")}
  display: flex;
  flex-direction: column;
  gap: 16px;
  z-index: 10;

  @media (max-width: 1280px) {
    ${(props) => (props.isRTL ? "right: 45px;" : "left: 45px;")}
  }
`;

const ReportsTable = (props) => {
  const navigate = useNavigate();
  const { setSelected, selected } = useContext(selectedContext);
  const { rows, currentStatus } = props;
  const { t, i18n } = useTranslation("reports");
  const { language } = i18n;
  const gridInstance = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAWB, setSelectedAWB] = useState(null);

  const handleMenuOpen = (event, awb) => {
    setAnchorEl(event.currentTarget);
    setSelectedAWB(awb);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAWB(null);
  };
  const toolbarClick = (args) => {
    if (args.item.text === "Excel Export") {
      gridInstance.current.excelExport();
    } else if (args.item.text === "CSV Export") {
      gridInstance.current.csvExport();
    }
  };

  const handleExport = (type) => {
    if (type === "excel") {
      gridInstance.current.excelExport();
    } else if (type === "csv") {
      gridInstance.current.csvExport();
    }
  };

  const handleRowSelection = (e) => {
    if (e.isHeaderCheckboxClicked === true) {
      if (Array.isArray(e.data)) {
        setSelected([...selected, ...e.data.map((shipment) => shipment.AWB)]);
      } else {
        setSelected([...selected, e.data.AWB]);
      }
    } else {
      setSelected((prevSelected) => [...prevSelected, e.data.AWB]);
    }
  };

  const handleRowDeSelection = (e) => {
    if (e.isHeaderCheckboxClicked === true) {
      setSelected([]);
    } else {
      setSelected(selected.filter((shipment) => shipment !== e.data.AWB));
    }
  };

  return (
    <Box
      sx={{
        width: "95%",
        marginLeft: language === "en" ? "50px" : "0px",
        marginRight: language === "ar" ? "50px" : "0px",
      }}
    >
      <ExportContainer isRTL={i18n.language === "ar"}>
        <ExportButton onClick={() => handleExport("excel")}>
          <img src={ExcelExportIcon} width={20} alt="" />
          {t("Export_Excel")}
          <img src={FrameDown} alt="" width={20} />
        </ExportButton>
      </ExportContainer>

      <GridComponent
        enableRtl={language === "ar"}
        dataSource={rows}
        rowSelected={handleRowSelection}
        rowDeselected={handleRowDeSelection}
        selectionSettings={{ type: "checkbox" }}
        ref={gridInstance}
        // toolbar={["ExcelExport", "CsvExport"]}
        filterSettings={{ type: "Excel" }}
        allowSorting
        allowExcelExport
        enableStickyHeader
        allowFiltering
        allowGrouping
        toolbarClick={toolbarClick}
        allowPaging={true}
        pageSettings={{ pageCount: 5, pageSize: 10 }}
        tooltip={{ enable: true }}
        excelQueryCellInfo={(args) => {
          if (args.column.field === "ToConsigneeName") {
            args.value = `${args.data.ToConsigneeName || ""}\n${
              args.data.ToPhone || ""
            }${args.data.ToMobile ? ` - ${args.data.ToMobile}` : ""}`;
          }
          if (args.column.field === "Location") {
            args.value = `${args.data.ToCityName || ""}\n${
              args.data.ToAddress || ""
            }`;
          }
          if (args.column.field === "COD") {
            args.value = `${args.data.COD || 0} ج.م\nالدفع عند الاستلام`;
          }
        }}
      >
        <ColumnsDirective>
          <ColumnDirective width={70} type="checkbox" />
          <ColumnDirective
            template={(e) => {
              return (
                <CustomLink
                  to={`/shipment/${e.AWB}`}
                  style={{
                    color: "black", // أحمر ثابت
                    textDecoration: "none",
                    cursor: "pointer",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontWeight: "bold",
                    transition: "all 0.3s ease",
                    backgroundColor: "#ffe5e5",
                  }}
                  onMouseEnter={(event) => {
                    event.target.style.backgroundColor = "#ffe9e9"; // أحمر فاتح عند الهوفر
                  }}
                  onMouseLeave={(event) => {
                    event.target.style.backgroundColor = "transparent";
                  }}
                >
                  {e.AWB}
                </CustomLink>
              );
            }}
            field="AWB"
            headerText={t("awb")}
            width="150"
            clipMode="EllipsisWithTooltip"
          />

          <ColumnDirective
            field="ToConsigneeName"
            headerText={t("consignee")}
            width="200"
            clipMode="EllipsisWithTooltip"
            template={(e) => (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontWeight: "bold" }}>{e.ToConsigneeName}</span>
                <span style={{ color: "#555", fontSize: "12px" }}>
                  {e.ToPhone} {e.ToMobile ? `- ${e.ToMobile}` : ""}
                </span>
              </div>
            )}
          />

          <ColumnDirective
            field="Location"
            headerText={t("address")}
            width="200"
            clipMode="EllipsisWithTooltip"
            template={(e) => (
              <div style={{ display: "flex", flexDirection: "column" }}>
                <span style={{ fontWeight: "bold" }}>{e.ToCityName}</span>
                <span style={{ color: "#555", fontSize: "12px" }}>
                  {e.ToAddress}
                </span>
              </div>
            )}
          />

          <ColumnDirective
            field="StatusNameA"
            headerText={t("status")}
            width="150"
            clipMode="EllipsisWithTooltip"
            template={(e) => (
              <span
                style={{
                  backgroundColor:
                    e.StatusID === 5
                      ? "#d0f2d0"
                      : e.StatusNameA.includes("Return")
                      ? "#fdd"
                      : "#eee",
                  color:
                    e.StatusNameA === "Shipped"
                      ? "green"
                      : e.StatusNameA.includes("Return")
                      ? "red"
                      : "#333",
                  padding: "4px 10px",
                  borderRadius: "12px",
                  fontWeight: "bold",
                  fontSize: "13px",
                }}
              >
                {e.StatusNameA}
              </span>
            )}
          />

          <ColumnDirective
            field="ReasonNameA"
            headerText={t("reason")}
            width="150"
            clipMode="EllipsisWithTooltip"
            template={(props) => (
              <span>{props.StatusID === 5 ? "" : props.ReasonNameA}</span>
            )}
          />

          <ColumnDirective
            field="COD"
            headerText={t("COD")}
            width="200"
            clipMode="EllipsisWithTooltip"
            template={(e) => (
              <div style={{ display: "flex", alignItems: "center" }}>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    marginRight: "10px",
                  }}
                >
                  <span style={{ fontWeight: "bold", marginBottom: "5px" }}>
                    {e.COD} ج.م
                  </span>
                  <div>الدفع عند الاستلام</div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    backgroundColor: "#e0f7fa",
                    marginLeft: "10px",
                  }}
                >
                  <img
                    src={Money}
                    alt="money-icon"
                    style={{ width: "20px", height: "20px" }}
                  />
                </div>
              </div>
            )}
          />

          <ColumnDirective
            field="PickupDate"
            headerText={t("PickupDate")}
            width="150"
            clipMode="EllipsisWithTooltip"
          />
          <ColumnDirective
            field="AuditDate"
            headerText={t("Last_Update")}
            width="150"
            clipMode="EllipsisWithTooltip"
          />
          <ColumnDirective
            field="ToRef"
            headerText={t("Ref")}
            width="120"
            clipMode="EllipsisWithTooltip"
          />
          <ColumnDirective
            field="TotalAttempts"
            headerText={t("Attempts")}
            width="150"
            clipMode="EllipsisWithTooltip"
          />
          <ColumnDirective
            field="ShipmentTypeNameA"
            headerText={t("Shipment Type")}
            width="160"
            clipMode="EllipsisWithTooltip"
          />
          <ColumnDirective
            field="ReturnAWB"
            headerText={t("Returned Code Number")}
            width="190"
            clipMode="EllipsisWithTooltip"
          />
          <ColumnDirective
            field="UpdateNotes"
            headerText={t("UpdateNotes")}
            width="160"
            clipMode="EllipsisWithTooltip"
          />
          <ColumnDirective
            headerText="Actions"
            width="150"
            field="actions"
            textAlign="Center"
            template={(rowData) => (
              <IconButton onClick={(e) => handleMenuOpen(e, rowData.AWB)}>
                <MoreVertIcon />
              </IconButton>
            )}
          />
        </ColumnsDirective>

        <Inject
          services={[Toolbar, ExcelExport, Page, Filter, Selection, Sort]}
        />
      </GridComponent>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleMenuClose();

            console.log("تعديل الشحنة", selectedAWB);

            const selectedShipmentData = rows.find(
              (row) => row.AWB === selectedAWB
            );

            if (selectedShipmentData) {
              navigate("/edit-shipment", {
                state: {
                  shipmentData: selectedShipmentData,
                },
              });
              console.log("🚀 selectedShipmentData", selectedShipmentData);
            }
          }}
          style={{ fontFamily: "cairo", fontWeight: "bold" }}
        >
          {t("edit")}
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleMenuClose();
            console.log("إضافة شكوى", selectedAWB);
            // openComplainModal(selectedAWB);
          }}
          style={{ fontFamily: "cairo", fontWeight: "bold" }}
        >
          Add Ticket
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            console.log("إلغاء الشحنة", selectedAWB);
            // confirmCancelShipment(selectedAWB);
          }}
          style={{ color: "red", fontFamily: "cairo", fontWeight: "900" }}
        >
          Cancel
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ReportsTable;
